import { useEffect, useMemo, useState } from 'react';

import { Account, Allocation, AllocationType, Strategy } from '@ndvr/portfolio-api';

import { Accordion } from '@/components/ui/accordion';
import { useCreateAllocation } from 'views/portfolio-engine/allocation-builder/allocations/allocation-queries';
import { EditAllocationDialog } from 'views/portfolio-engine/allocation-builder/allocations/edit-dialog/EditAllocationDialog';

import { AllocationTabFilter } from './AccountAllocationEditor';
import { AccountAllocationItem } from './AccountAllocationItem';
import { usePinnedAllocationState } from './usePinnedAllocationState';

interface PinnedAllocationViewProps {
    initialAccounts: Account[];
    cardIsEditing: boolean;
    allocations: Allocation[];
    isLoadingAllocations: boolean;
    isLoadingPermissions?: boolean;
    assignableAllocations: Record<string, Allocation>;
    accountAllocations: Record<string, Allocation | undefined>;
    onAccountSelect?: (accountGUID: string | undefined) => void;
    onAccountsChange: (accounts: Account[]) => void;
    canCreateSharedAllocation?: boolean;
    availableStrategies: Strategy[];
}

export const PinnedAllocationView = ({
    initialAccounts,
    cardIsEditing,
    allocations,
    isLoadingAllocations,
    isLoadingPermissions,
    assignableAllocations,
    accountAllocations,
    onAccountSelect,
    onAccountsChange,
    canCreateSharedAllocation,
    availableStrategies,
}: PinnedAllocationViewProps) => {
    const [allocationTypeFilter, setAllocationTypeFilter] = useState<AllocationTabFilter>(AllocationType.SHARED);

    const { mutate: createAllocation, isPending: isSaving } = useCreateAllocation();

    const {
        accounts,
        openAccountGUID,
        isDialogOpen,
        editingAllocation,
        handleAccountAllocationChange,
        handleAccountManagementTypeChange,
        handleAccountCashEqSweepChange,
        handleAccordionChange,
        handleCreateAllocation,
        handleCloneAllocation,
        handleDialogClose,
        handleAllocationDialogSave,
    } = usePinnedAllocationState({
        initialAccounts,
        cardIsEditing,
        allocations,
        onAccountSelect,
        createAllocation,
        canCreateSharedAllocation,
    });

    // Notify parent of account changes
    useEffect(() => {
        onAccountsChange(accounts);
    }, [accounts, onAccountsChange]);

    // Initialize filter based on open account's allocation when entering edit mode or opening an account
    useEffect(() => {
        if (cardIsEditing && openAccountGUID) {
            const account = accounts.find((acc) => acc.accountGUID === openAccountGUID);
            const allocationGUID = account?.allocationGUID;
            const allocation = allocationGUID
                ? (assignableAllocations[allocationGUID] ?? accountAllocations[account?.accountGUID ?? ''])
                : undefined;
            if (allocation) {
                setAllocationTypeFilter(allocation.inferred ? 'INFERRED' : allocation.allocationType);
            }
        }
    }, [cardIsEditing, openAccountGUID, accounts, assignableAllocations, accountAllocations]);

    const filteredAllocations = useMemo(
        () =>
            allocationTypeFilter === 'INFERRED'
                ? []
                : allocations.filter((allocation) => allocation.allocationType === allocationTypeFilter),
        [allocations, allocationTypeFilter],
    );

    const handleAllocationTypeFilterChange = (newType: AllocationTabFilter) => {
        setAllocationTypeFilter(newType);
        // Clear selection when filter changes
        if (openAccountGUID) {
            handleAccountAllocationChange(openAccountGUID, undefined);
        }
    };

    if (accounts.length === 0) {
        return <div className="py-4 text-center text-sm text-gray-500">No accounts available</div>;
    }

    return (
        <>
            <Accordion
                type="single"
                collapsible
                value={openAccountGUID ?? ''}
                onValueChange={handleAccordionChange}
                className="w-full"
            >
                {accounts.map((account) => {
                    const resolvedAllocation = account.allocationGUID
                        ? (assignableAllocations[account.allocationGUID] ?? accountAllocations[account.accountGUID])
                        : undefined;
                    const savedAllocation = accountAllocations[account.accountGUID];
                    const inferredAllocation = savedAllocation?.inferred ? savedAllocation : undefined;

                    return (
                        <AccountAllocationItem
                            key={account.accountGUID}
                            account={account}
                            accountAllocation={resolvedAllocation}
                            inferredAllocation={inferredAllocation}
                            cardIsEditing={cardIsEditing}
                            allocationTypeFilter={allocationTypeFilter}
                            filteredAllocations={filteredAllocations}
                            isLoadingAllocations={isLoadingAllocations}
                            isLoadingPermissions={isLoadingPermissions}
                            onAllocationTypeFilterChange={handleAllocationTypeFilterChange}
                            onAccountManagementTypeChange={handleAccountManagementTypeChange}
                            onAccountCashEqSweepChange={handleAccountCashEqSweepChange}
                            onAccountAllocationChange={handleAccountAllocationChange}
                            onCreateAllocation={handleCreateAllocation}
                            onCloneAllocation={handleCloneAllocation}
                        />
                    );
                })}
            </Accordion>
            <EditAllocationDialog
                open={isDialogOpen}
                allocation={editingAllocation}
                onSave={handleAllocationDialogSave}
                onCancel={handleDialogClose}
                isSaving={isSaving}
                canCreateSharedAllocation={canCreateSharedAllocation}
                existingAllocationNames={allocations.map((a) => a.allocationName)}
                availableStrategies={availableStrategies}
            />
        </>
    );
};
